import React, { useState, useRef, memo, useCallback, useMemo, useEffect } from 'react';
import './index.less';
import { ConfigProvider, Modal, Typography, Carousel } from 'antd';
import classNames from 'classnames';
import queryDomMethods from '@/utils/queryDomMethods';
import { CarouselRef } from 'antd/lib/carousel';
import BadgeAnimation from './BadgeAnimation';
import { IPredictScoreCompletedExamPoint } from '@/types';

const { Paragraph } = Typography;

type IList = IPredictScoreCompletedExamPoint['knowledgeList'][];

interface IProps {
    dataSource: {
        count: number;
        list: IList;
    };
    onClose: () => void;
}

const modalStyles: { [key: string]: React.CSSProperties } = {
    content: { padding: 0, background: 'transparent', boxShadow: 'none', border: 'none', borderRadius: 0 },
    body: {
        height: '600px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
    },
};

const CompletedCarousel = memo(({ list, count, carouselRef }: { list: IList; count: number; carouselRef: any }) => (
    <>
        <div className="sub-tiku__exam-point-completed-content">
            {list.length > 1 && (
                <>
                    <img
                        alt="Left"
                        className="sub-tiku__exam-point-completed-arrow-left"
                        onClick={() => carouselRef.current?.prev()}
                        src="https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202210/23/f9ffd_20221023144620.png"
                    />
                    <img
                        alt="Right"
                        className="sub-tiku__exam-point-completed-arrow-right"
                        onClick={() => carouselRef.current?.next()}
                        src="https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202210/23/21a78_20221023144459.png"
                    />
                </>
            )}
            <Carousel ref={carouselRef} dots={list.length > 1}>
                {list.map((item, index) => (
                    <div key={index}>
                        <div className="sub-tiku__exam-point-completed-list">
                            {item.map(child => (
                                <div
                                    key={child.knowledgeId}
                                    className={classNames('sub-tiku__exam-point-completed-list-item', {
                                        'sub-tiku__exam-point-completed-list-item-small': count > 2,
                                    })}
                                >
                                    <Paragraph ellipsis={{ rows: 2, expandable: false }}>{child.title}</Paragraph>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </Carousel>
        </div>
    </>
));

const CompletedAnimation: React.FC<IProps> = ({ dataSource, onClose }) => {
    const carouselRef = useRef<CarouselRef>(null);
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [contentVisible, setContentVisible] = useState(false);
    const specificFrameRef = useRef(55);

    const handleFrameChange = useCallback((frame: number) => {
        console.log('🚀 ~ handleFrameChange ~ frame:', frame);

        if (frame === specificFrameRef.current) {
            setContentVisible(true);
        }
    }, []);

    const renderCompletedContent = () => {
        return (
            <div className={classNames('sub-tiku__exam-point-completed-wrap', { visible: contentVisible })}>
                <div className="sub-tiku__exam-point-completed-background" />
                <div className="sub-tiku__exam-point-completed">
                    <div className="sub-tiku__exam-point-completed-number">
                        <span className="em">{dataSource.count}</span>&nbsp;&nbsp;个
                    </div>
                    <CompletedCarousel list={dataSource.list} count={dataSource.count} carouselRef={carouselRef} />
                </div>
                <div
                    className="sub-tiku__exam-point-completed-closeicon"
                    onClick={() => {
                        setIsModalOpen(false);
                        onClose();
                    }}
                ></div>
            </div>
        );
    };

    return (
        <ConfigProvider theme={{ token: { motion: false, motionDurationMid: '0s', motionDurationSlow: '0s' } }}>
            <Modal
                centered
                closable={false}
                footer={null}
                keyboard={false}
                maskClosable={false}
                title={null}
                open={isModalOpen}
                width={600}
                getContainer={queryDomMethods.querySubTikuRoot}
                styles={modalStyles}
                destroyOnClose
            >
                {/* 第一部分动画 */}
                <BadgeAnimation onFrameChange={handleFrameChange} />

                {/* 第二部分动画 */}
                {renderCompletedContent()}
            </Modal>
        </ConfigProvider>
    );
};

export default memo(CompletedAnimation);
